// Identity belongs to the action, not the summary text or a status timestamp.
export const conversationMessageKey = (message) => {
  const meta = message.meta || {};
  if (meta.outbound_attempt_id) return `chat-outbound:${meta.outbound_attempt_id}`;
  if (meta.type === 'voice_call' && meta.call_id) return `voice:${meta.call_id}`;
  if (meta.type === 'function_result' && ['send_sms', 'post_call_sms'].includes(meta.function)) {
    if (meta.function === 'post_call_sms' && meta.call_id) return `sms:post:${meta.call_id}`;
    if (meta.sms_attempt_id) return `sms:${meta.sms_attempt_id}`;
  }
  return message.event_id || message.message_id || message.id || `${message.timestamp || ''}|${message.role || ''}|${message.text || ''}`;
};

const smsProgress = { processing: 0, submitting: 1, unknown: 2, skipped: 3, failed: 3, accepted: 4, delivered: 5 };
const placeholderSummary = (text) => !text || ['Call answered.', 'Call completed.'].includes(text.trim());
const isMessage = message => ['agent', 'customer', 'automation'].includes(message.role);
const providerMessageId = message => message.message_id || message.ghl_message_id || message.meta?.message_id || message.id;
const legacyMessageKey = message => `${message.timestamp || ''}|${message.role || ''}|${message.text || ''}`;

export const orderConversationMessages = (messages) => {
  const sorted = [...messages].sort((a, b) => (Date.parse(a.timestamp) || 0) - (Date.parse(b.timestamp) || 0));
  const targets = new Map(sorted.filter(isMessage).map(m => [providerMessageId(m), m]));
  const before = new Map();
  const attached = new Set();
  sorted.forEach(event => {
    if (event.meta?.type !== 'function_result') return;
    const id = event.meta?.details?.message_id || event.meta?.related_message_id;
    const target = id && targets.get(id);
    if (!target) return;
    before.set(target, [...(before.get(target) || []), event]);
    attached.add(event);
  });
  // Keep unmatched/failed events chronological. Only move an action when its
  // exact provider message ID identifies the corresponding message.
  return sorted.flatMap(message => attached.has(message) ? [] : [...(before.get(message) || []), message]);
};

const mergeOne = (old, incoming) => {
  if (!old) return incoming;
  const before = old.meta || {};
  const after = { ...(incoming.meta || {}) };
  if (['delivered', 'read', 'failed'].includes(before.delivery_status) && ['sending', 'accepted', 'sent'].includes(after.delivery_status)) {
    after.delivery_status = before.delivery_status;
    after.delivery_reason = before.delivery_reason;
  }
  if (after.type === 'voice_call') {
    return { ...old, ...incoming,
      text: placeholderSummary(incoming.text) && !placeholderSummary(old.text) ? old.text : incoming.text,
      meta: { ...before, ...after,
        transcript: after.transcript?.length ? after.transcript : before.transcript,
        has_recording: after.has_recording || before.has_recording,
        duration: after.duration || before.duration } };
  }
  if (before.details?.sms_status && after.details?.sms_status) {
    const oldTime = Date.parse(old.updated_at || old.timestamp) || 0;
    const newTime = Date.parse(incoming.updated_at || incoming.timestamp) || 0;
    if (newTime < oldTime || (smsProgress[after.details.sms_status] < 2 && smsProgress[before.details.sms_status] >= 2)) return old;
  }
  return { ...old, ...incoming, message_id: incoming.message_id || old.message_id,
    meta: { ...before, ...after, details: { ...before.details, ...after.details } } };
};

export const mergeConversationMessages = (previous = [], incoming = []) => {
  const map = new Map();
  const aliases = new Map();
  [...previous, ...incoming].forEach(message => {
    let key = conversationMessageKey(message);
    if (isMessage(message)) {
      const signature = legacyMessageKey(message);
      const alias = aliases.get(signature);
      if (alias && alias !== key && (!providerMessageId(message) || !providerMessageId(map.get(alias) || {}))) {
        const old = map.get(alias);
        if (!providerMessageId(message)) key = alias;
        else map.delete(alias);
        map.set(key, old);
      }
      aliases.set(signature, alias && alias !== key && map.has(alias) ? null : key);
    }
    map.set(key, mergeOne(map.get(key), message));
  });
  return orderConversationMessages([...map.values()]);
};

export const smsStatusLabel = (status) => ({ accepted: 'Successful', delivered: 'Successful', failed: 'Failed',
  processing: 'Sending', submitting: 'Sending', unknown: 'Unconfirmed', skipped: 'Not sent' }[status] || 'Unconfirmed');